import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GeneratedScript = ({ script, onSave, onEdit, onRegenerate, onRate, canRate = false }) => {
  const [copied, setCopied] = useState(false);
  const [rated, setRated] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(script?.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleThumbsUp = async () => {
    if (!canRate || rated) return;
    
    try {
      await onRate(5);
      setRated(true);
    } catch (error) {
      console.error('Error rating script:', error);
    }
  };

  const handleThumbsDown = async () => {
    if (!canRate || rated) return;
    
    try {
      await onRate(1);
      setRated(true);
    } catch (error) {
      console.error('Error rating script:', error);
    }
  };

  const formatScriptContent = (content, type) => {
    if (type === 'email') {
      const lines = content?.split('\n');
      return (
        <div className="space-y-4">
          {lines?.map((line, index) => {
            if (line?.startsWith('Subject:')) {
              return (
                <div key={index} className="pb-3 border-b border-border">
                  <p className="text-sm font-semibold text-foreground">{line}</p>
                </div>
              );
            }
            if (line?.trim() === '') return null;
            return (
              <p key={index} className="text-sm text-foreground leading-relaxed">
                {line}
              </p>
            );
          })}
        </div>
      );
    }

    if (type === 'call') {
      const sections = content?.split('\n\n');
      return (
        <div className="space-y-4">
          {sections?.map((section, index) => {
            const lines = section?.split('\n');
            const title = lines?.[0];
            const body = lines?.slice(1);
            
            return (
              <div key={index} className="space-y-2">
                <h4 className="text-sm font-semibold text-primary">{title}</h4>
                {body?.map((line, lineIndex) => (
                  <p key={lineIndex} className="text-sm text-foreground leading-relaxed pl-4">
                    {line}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      );
    }

    return <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{content}</p>;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Icon name={script?.icon} size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-foreground">{script?.title}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{script?.situation}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            title="Copy to clipboard"
          >
            <Icon name={copied ? 'Check' : 'Copy'} size={18} color={copied ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
          </button>
        </div>
      </div>
      <div className="bg-background rounded-lg p-4 border border-border">
        {formatScriptContent(script?.content, script?.type)}
      </div>

      {canRate && !rated && (
        <div className="flex items-center justify-center gap-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
          <p className="text-sm font-medium text-foreground">Was this script helpful?</p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleThumbsUp}
              className="p-3 rounded-lg bg-success/10 hover:bg-success/20 border border-success/30 transition-all hover:scale-105"
              title="This was helpful"
            >
              <Icon name="ThumbsUp" size={20} color="var(--color-success)" />
            </button>
            <button
              onClick={handleThumbsDown}
              className="p-3 rounded-lg bg-error/10 hover:bg-error/20 border border-error/30 transition-all hover:scale-105"
              title="Needs improvement"
            >
              <Icon name="ThumbsDown" size={20} color="var(--color-error)" />
            </button>
          </div>
        </div>
      )}

      {rated && (
        <div className="flex items-center justify-center gap-2 p-3 bg-success/10 rounded-lg border border-success/20">
          <Icon name="Check" size={18} color="var(--color-success)" />
          <p className="text-sm font-medium text-success">Thank you for your feedback!</p>
        </div>
      )}

      {script?.keyPoints && script?.keyPoints?.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
            Key Talking Points
          </h4>
          <ul className="space-y-2">
            {script?.keyPoints?.map((point, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {script?.tips && script?.tips?.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Target" size={16} color="var(--color-primary)" />
            Strategic Tips
          </h4>
          <ul className="space-y-2">
            {script?.tips?.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Icon name="ArrowRight" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
        <Button variant="default" iconName="Save" iconPosition="left" onClick={onSave}>
          Save to Playbook
        </Button>
        <Button variant="outline" iconName="Edit" iconPosition="left" onClick={onEdit}>
          Edit Script
        </Button>
        <Button variant="ghost" iconName="RefreshCw" iconPosition="left" onClick={onRegenerate}>
          Regenerate
        </Button>
      </div>
    </div>
  );
};

export default GeneratedScript;